import { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';
import { useAuthStore } from '@/store/authStore';
import authApi, { type OAuthProvidersResponse } from '@/services/auth';
import { config } from '@/config';
import { loadAppleAuthSDK, initAppleAuth, signInWithApple } from '@/utils/appleAuth';

/**
 * Microsoft Logo Component
 * Official Microsoft square logo with 4 colored squares
 * Following Microsoft branding guidelines
 */
function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Apple Logo Component
 * Following Apple branding guidelines for Sign in with Apple
 * White logo on black button (as specified by Apple HIG)
 */
function AppleLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.24 0-1.44.64-2.2.52-3.06-.4-4.84-5.02-4.12-12.67 1.34-12.96 1.38.07 2.34.8 3.08.8.74 0 2.13-.94 3.6-.8.62.02 2.33.24 3.44 1.84-3.16 1.94-2.64 5.64.5 7.12-.6 1.56-1.4 3.1-2.58 4zm-3.12-15.48c-.94 1.12-2.5 1.98-4.02 1.86-.2-1.5.56-3.08 1.42-4.06.96-1.08 2.58-1.88 3.92-1.92.16 1.58-.46 3.12-1.32 4.12z"/>
    </svg>
  );
}

export interface SocialLoginButtonsProps {
  /**
   * Callback when Google login succeeds (optional - uses auth store by default)
   */
  onGoogleSuccess?: (credential: string) => void;
  /**
   * Callback when Apple login succeeds in register mode
   * WP-FE-AUTH-1: Receives idToken plus optional user info (Apple only provides name on first auth)
   */
  onAppleSuccess?: (idToken: string, firstName?: string, lastName?: string) => void;
  /**
   * Callback when Microsoft login is initiated (optional)
   */
  onMicrosoftClick?: () => void;
  /**
   * Callback when any social login fails
   */
  onError?: (error: string) => void;
  /**
   * Whether buttons are disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether terms of service have been accepted (required for registration, optional for login)
   * When provided, this value is sent to the backend with OAuth requests.
   * For new user registration via OAuth, the backend requires terms_accepted=true.
   */
  termsAccepted?: boolean;
  /**
   * Device fingerprint for MFA device trust (WP-AUTH-7)
   * When provided, the backend can check if this device is already trusted
   * and skip MFA for returning trusted devices.
   */
  deviceFingerprint?: string;
  /**
   * Mode of operation: 'login' for existing users, 'register' for new users.
   * In 'register' mode, the onGoogleSuccess/onAppleSuccess callback is called to show terms modal.
   * In 'login' mode (default), calls loginWithGoogle/loginWithApple directly.
   *
   * This explicit mode prop is needed because Google's SDK captures callbacks
   * at mount time, which can cause stale closure issues when checking if
   * onGoogleSuccess is defined.
   */
  mode?: 'login' | 'register';
}

/**
 * Social Login Buttons Component
 * Only shows OAuth providers that are enabled and properly configured.
 * Currently supports Google Sign-In and Microsoft Sign-In when configured.
 * Apple Sign-In planned for Phase 6 (mobile apps).
 *
 * Security: ID tokens are sent to backend for validation.
 * No PII is logged to console.
 */
export default function SocialLoginButtons({
  onGoogleSuccess,
  onAppleSuccess,
  onMicrosoftClick,
  onError,
  disabled = false,
  className,
  termsAccepted,
  deviceFingerprint,
  mode = 'login',
}: SocialLoginButtonsProps) {
  const { loginWithGoogle, loginWithApple, error, clearError } = useAuthStore();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleSdkReady, setAppleSdkReady] = useState(false);
  const [providers, setProviders] = useState<OAuthProvidersResponse | null>(null);
  const [providersLoading, setProvidersLoading] = useState(true);

  // Use ref to store callback to avoid stale closure issues with Google SDK.
  // The Google Sign-In SDK captures callbacks at mount time, so checking
  // `if (onGoogleSuccess)` inside the callback may see a stale value.
  // Using a ref ensures we always have the latest callback.
  const onGoogleSuccessRef = useRef(onGoogleSuccess);
  onGoogleSuccessRef.current = onGoogleSuccess;

  // Use ref for Apple callback as well to avoid stale closure issues
  const onAppleSuccessRef = useRef(onAppleSuccess);
  onAppleSuccessRef.current = onAppleSuccess;

  // Fetch OAuth providers status from backend
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await authApi.getOAuthProviders();
        setProviders(response);
      } catch {
        // If we can't fetch providers, assume none are available
        setProviders({ googleEnabled: false, appleEnabled: false, microsoftEnabled: false });
      } finally {
        setProvidersLoading(false);
      }
    };

    fetchProviders();
  }, []);

  // Initialize Apple Sign In SDK when provider is enabled
  // WP-FE-AUTH-1: Apple Sign In Web Frontend Integration
  useEffect(() => {
    const initApple = async () => {
      // Don't initialize if not configured or not enabled
      if (!config.appleClientId || !providers?.appleEnabled) {
        return;
      }

      try {
        await loadAppleAuthSDK();
        initAppleAuth(config.appleClientId, config.appleRedirectUri, true);
        setAppleSdkReady(true);
      } catch (err) {
        // SDK load failed - Apple Sign In will not be available
        // Log warning but don't surface to user (graceful degradation)
        console.warn('Failed to initialize Apple Sign In SDK:', err instanceof Error ? err.message : 'Unknown error');
      }
    };

    // Only initialize after providers are loaded and Apple is enabled
    if (!providersLoading && providers?.appleEnabled) {
      initApple();
    }
  }, [providersLoading, providers?.appleEnabled]);

  // Check if Google OAuth is configured (both client-side and server-side)
  const isGoogleEnabled = Boolean(config.googleClientId) && providers?.googleEnabled === true;

  // Check if Microsoft OAuth is enabled (server-side only, no client ID needed)
  const isMicrosoftEnabled = providers?.microsoftEnabled === true;

  // Check if Apple OAuth is configured and SDK is ready
  // WP-FE-AUTH-1: Requires client ID, backend enabled, AND SDK loaded
  const isAppleEnabled = Boolean(config.appleClientId) && providers?.appleEnabled === true && appleSdkReady;

  /**
   * Handle successful Google login
   * Receives credential (ID token) from GoogleLogin component
   */
  const handleGoogleSuccess = useCallback(
    async (credentialResponse: CredentialResponse) => {
      // The credential is the JWT ID token from Google
      const idToken = credentialResponse.credential;

      if (!idToken) {
        onError?.('No credential received from Google');
        return;
      }

      setGoogleLoading(true);
      clearError();

      try {
        // Use explicit mode check instead of callback existence check.
        // This avoids stale closure issues with Google SDK's callback capture.
        if (mode === 'register') {
          // Registration mode: call the callback to show terms modal
          const callback = onGoogleSuccessRef.current;
          if (callback) {
            callback(idToken);
          } else {
            onError?.('Registration callback not provided');
          }
        } else {
          // Login mode: use default auth store handler
          // WP-AUTH-7: Device fingerprint enables MFA device trust check on backend
          await loginWithGoogle(idToken, termsAccepted, deviceFingerprint);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Google sign-in failed';
        onError?.(message);
      } finally {
        setGoogleLoading(false);
      }
    },
    [loginWithGoogle, onError, clearError, termsAccepted, deviceFingerprint, mode]
  );

  /**
   * Handle Google login error
   */
  const handleGoogleError = useCallback(() => {
    const message = 'Google sign-in was cancelled or failed';
    onError?.(message);
  }, [onError]);

  /**
   * Handle Microsoft login button click
   * Fetches the authorization URL and redirects to Microsoft
   */
  const handleMicrosoftClick = useCallback(async () => {
    setMicrosoftLoading(true);
    clearError();

    try {
      if (onMicrosoftClick) {
        // Custom handler provided
        onMicrosoftClick();
      } else {
        // Fetch authorization URL and redirect
        const authUrl = await authApi.getMicrosoftAuthUrl();
        window.location.href = authUrl;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initiate Microsoft sign-in';
      onError?.(message);
      setMicrosoftLoading(false);
    }
    // Note: Don't set loading to false on success since we're redirecting
  }, [onMicrosoftClick, onError, clearError]);

  /**
   * Handle Apple Sign In button click
   * WP-FE-AUTH-1: Apple Sign In Web Frontend Integration
   *
   * Opens Apple's authentication popup and handles the response.
   * In register mode, calls callback to show terms modal.
   * In login mode, calls loginWithApple directly.
   */
  const handleAppleSignIn = useCallback(async () => {
    setAppleLoading(true);
    clearError();

    try {
      const response = await signInWithApple();
      const { authorization, user } = response;

      // Apple only provides user info on FIRST authorization
      // Must capture and store this as it won't be available on subsequent sign-ins
      const firstName = user?.name?.firstName;
      const lastName = user?.name?.lastName;

      if (mode === 'register') {
        // Registration mode: call callback to show terms modal
        // Store the credential and user info for later use
        const callback = onAppleSuccessRef.current;
        if (callback) {
          callback(authorization.id_token, firstName, lastName);
        } else {
          onError?.('Registration callback not provided');
        }
      } else {
        // Login mode: call backend directly via auth store
        // Arguments: (idToken, termsAccepted, deviceFingerprint, firstName, lastName)
        await loginWithApple(
          authorization.id_token,
          termsAccepted,
          deviceFingerprint,
          firstName,
          lastName
        );
      }
    } catch (err) {
      // Handle user cancellation gracefully (don't show error)
      const message = err instanceof Error ? err.message : 'Apple sign-in failed';
      // Apple SDK throws specific error types for cancellation
      const isCancelled = message.includes('popup_closed') ||
                          message.includes('cancelled') ||
                          message.includes('The operation was canceled') ||
                          message.includes('user_cancelled');
      if (!isCancelled) {
        onError?.(message);
      }
    } finally {
      setAppleLoading(false);
    }
  }, [mode, termsAccepted, deviceFingerprint, clearError, loginWithApple, onError]);

  // Check if any provider is enabled
  const hasAnyProvider = isGoogleEnabled || isMicrosoftEnabled || isAppleEnabled;

  // Always render the component to prevent tree-shaking.
  // This is necessary for runtime OAuth configuration via window.APP_CONFIG.
  // Hide with CSS when loading or no providers available.
  const isHidden = providersLoading || !hasAnyProvider;

  return (
    <div className={cn('space-y-3', isHidden && 'hidden', className)}>
      {/* Error display */}
      {error && (
        <p className="text-sm text-red-600 text-center" role="alert">
          {error}
        </p>
      )}

      {/* Google Sign In - always rendered, hidden when not configured.
          This prevents tree-shaking when Google OAuth is not configured at build time
          but may be available at runtime via window.APP_CONFIG. */}
      <div className={cn('w-full flex justify-center', !isGoogleEnabled && 'hidden')}>
        {googleLoading || disabled ? (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled
            isLoading={googleLoading}
            leftIcon={
              !googleLoading ? (
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              ) : undefined
            }
          >
            {googleLoading ? 'Signing in...' : 'Continue with Google'}
          </Button>
        ) : (
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="outline"
            size="large"
            width={400}
            text="continue_with"
            shape="rectangular"
          />
        )}
      </div>

      {/* Microsoft Sign In - Only shown when enabled on backend */}
      {isMicrosoftEnabled && (
        <Button
          type="button"
          variant="outline"
          className="w-full bg-white hover:bg-gray-50 border-gray-300 text-gray-700 font-medium"
          disabled={disabled || microsoftLoading || googleLoading || appleLoading}
          isLoading={microsoftLoading}
          onClick={handleMicrosoftClick}
          leftIcon={
            !microsoftLoading ? (
              <MicrosoftLogo className="w-5 h-5" />
            ) : undefined
          }
        >
          {microsoftLoading ? 'Signing in...' : 'Sign in with Microsoft'}
        </Button>
      )}

      {/* Apple Sign In - WP-FE-AUTH-1: Apple Sign In Web Frontend Integration */}
      {isAppleEnabled && (
        <Button
          type="button"
          variant="outline"
          className="w-full bg-black hover:bg-gray-900 border-black text-white font-medium"
          disabled={disabled || appleLoading || googleLoading || microsoftLoading}
          isLoading={appleLoading}
          onClick={handleAppleSignIn}
          aria-label="Sign in with Apple"
          leftIcon={
            !appleLoading ? (
              <AppleLogo className="w-5 h-5" />
            ) : undefined
          }
        >
          {appleLoading ? 'Signing in...' : 'Sign in with Apple'}
        </Button>
      )}
    </div>
  );
}
